import React, { useState, useEffect, useRef } from "react";
import { EmailMessage, SystemUser } from "@/api/entities";
import { invokeFunctions, UploadFile } from "@/api/integrations";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Send, RefreshCw, ArrowDownLeft, ArrowUpRight, Loader2, Paperclip, X, ChevronDown, ChevronUp, ExternalLink, Check, Eye, MousePointerClick, Bold, Italic, Underline, Link2, List, ListOrdered, UserPlus, Sparkles, Printer } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useToast } from "@/components/ui/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";

// #20 Construye una URL del webmail cPanel que, tras el login, abre Roundcube
// directamente con una búsqueda por el email del contacto. Si no hay email,
// cae a la home del webmail como antes.
const buildWebmailUrl = (email) => {
  const base = 'https://estudiroig.com:2096';
  if (!email) return `${base}/`;
  // Roundcube quicksearch: _task=mail + _action=search + _q=email + _mbox=INBOX + _scope=all
  // (búsqueda en TODAS las carpetas — encuentra hilos archivados / en Sent también).
  const inner = `/?_task=mail&_action=search&_q=${encodeURIComponent(email)}&_mbox=INBOX&_scope=all`;
  return `${base}/?goto_app=Roundcube&goto_uri=${encodeURIComponent(inner)}`;
};

export default function EmailConversation({ lead, expandEmailId }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCompose, setShowCompose] = useState(false);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState(""); // fuente de verdad del editor (HTML)
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [pastedImages, setPastedImages] = useState([]); // [{ cid, dataUrl, filename, contentType }]
  const [attachments, setAttachments] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [expandedEmails, setExpandedEmails] = useState({});
  const [replyToMessageId, setReplyToMessageId] = useState(null);
  const [references, setReferences] = useState(null);
  const editorRef = useRef(null);
  const [showCcPicker, setShowCcPicker] = useState(false);
  const [showBccPicker, setShowBccPicker] = useState(false);
  // #17 Sugerencia IA
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [sugerencia, setSugerencia] = useState(null);

  const { data: crmUsers = [] } = useQuery({
    queryKey: ['system-users'],
    queryFn: () => SystemUser.list(),
    staleTime: 1000 * 60 * 30,
  });

  // Cuando se abre el panel y cambia el contenido inicial, sembrar el editor.
  useEffect(() => {
    if (showCompose && editorRef.current) {
      editorRef.current.innerHTML = bodyHtml || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCompose]);

  const exec = (cmd, value) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
    setBodyHtml(editorRef.current?.innerHTML || "");
  };
  const promptLink = () => {
    const url = window.prompt("URL:", "https://");
    if (url) exec('createLink', url);
  };

  // Sanitización mínima del HTML pegado: quita tags peligrosos / inútiles y
  // atributos que rompan el renderizado del email (scripts, eventos, class
  // proprietary, comentarios MS Office). Conserva estilos inline básicos.
  const sanitizePastedHtml = (html) => {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc.querySelectorAll('script, style, meta, link, title, head, base, object, embed, iframe').forEach(n => n.remove());
      const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT);
      const toClean = [];
      let cur = walker.nextNode();
      while (cur) { toClean.push(cur); cur = walker.nextNode(); }
      toClean.forEach(el => {
        for (const attr of Array.from(el.attributes)) {
          const n = attr.name.toLowerCase();
          if (n.startsWith('on') || n === 'class' || n === 'id' || n.startsWith('data-mce') || n.startsWith('data-office')) {
            el.removeAttribute(attr.name);
          }
        }
      });
      return doc.body.innerHTML
        // quita comentarios condicionales de MS Office
        .replace(/<!--[\s\S]*?-->/g, '')
        // colapsa varios <br> consecutivos
        .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>');
    } catch {
      return html;
    }
  };

  const handlePaste = (e) => {
    const cd = e.clipboardData;
    if (!cd) return;

    // 1) Si viene una imagen en el clipboard (captura, etc.), igual que antes:
    //    se inserta inline con CID y se adjunta al email.
    const items = cd.items;
    if (items) {
      for (const item of items) {
        if (item.type && item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (!file) continue;
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = String(reader.result || "");
            const cid = `paste${Date.now()}${Math.floor(Math.random() * 1000)}@cerca`;
            const ext = (item.type.split('/')[1] || 'png').replace('+xml', '');
            const filename = `screenshot-${Date.now()}.${ext}`;
            setPastedImages(prev => [...prev, { cid, dataUrl, filename, contentType: item.type }]);
            document.execCommand('insertHTML', false,
              `<img src="${dataUrl}" data-cid="${cid}" alt="${filename}" style="max-width:100%; height:auto;" />`
            );
            setBodyHtml(editorRef.current?.innerHTML || "");
          };
          reader.readAsDataURL(file);
          return;
        }
      }
    }

    // 2) Si viene HTML formateado (de una web/email), lo insertamos
    //    sanitizado para conservar negritas/listas/enlaces/colores.
    const html = cd.getData('text/html');
    if (html && html.trim()) {
      e.preventDefault();
      const clean = sanitizePastedHtml(html);
      document.execCommand('insertHTML', false, clean);
      setBodyHtml(editorRef.current?.innerHTML || "");
      return;
    }
    // 3) Texto plano → comportamiento default del navegador (no preventDefault).
  };

  const buildSendableHtml = () => {
    let html = editorRef.current?.innerHTML || bodyHtml || "";
    for (const img of pastedImages) {
      const escaped = img.dataUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      html = html.replace(new RegExp(`src="${escaped}"`, 'g'), `src="cid:${img.cid}"`);
    }
    return html;
  };

  const addAddressToField = (field, addr) => {
    const cur = field === 'cc' ? cc : bcc;
    const next = cur ? (cur.includes(addr) ? cur : `${cur}, ${addr}`) : addr;
    if (field === 'cc') setCc(next); else setBcc(next);
  };

  const toggleEmailExpanded = (emailId) => {
    setExpandedEmails(prev => ({
      ...prev,
      [emailId]: !prev[emailId]
    }));
  };

  const { data: emails = [], isLoading } = useQuery({
    queryKey: ['emails', lead.id],
    queryFn: () => EmailMessage.list({ lead_id: lead.id }, { orderBy: 'date', orderDirection: 'desc' }),
    enabled: !!lead.id,
  });

  // Auto-expandir email si viene de la URL
  useEffect(() => {
    if (expandEmailId && emails.length > 0) {
      if (expandEmailId === 'true') {
        // Expandir el primer email (más reciente)
        setExpandedEmails(prev => ({ ...prev, [emails[0].id]: true }));
      } else {
        // Expandir el email específico
        setExpandedEmails(prev => ({ ...prev, [expandEmailId]: true }));
      }
    }
  }, [expandEmailId, emails]);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await invokeFunctions('fetchEmails', {});
      queryClient.invalidateQueries({ queryKey: ['emails', lead.id] });
      toast({
        title: "Sincronizado",
        description: "Emails actualizados",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron sincronizar los emails",
        variant: "destructive"
      });
    }
    setIsSyncing(false);
  };

  // Imprime el hilo completo en una vista A4. Mismo patrón que el botón Imprimir de
  // presupuestos (PresupuestoGenerator): window.open + HTML autocontenido + print().
  // Sin CSS global ni riesgo para los estilos de la app.
  const handlePrint = () => {
    if (!emails.length) {
      toast({ title: "Sin emails", description: "No hay emails para imprimir." });
      return;
    }
    const win = window.open("", "_blank", "width=900,height=1100");
    if (!win) {
      toast({
        title: "Bloqueo de pop-ups",
        description: "Permite las ventanas emergentes para este sitio e inténtalo de nuevo.",
        variant: "destructive",
      });
      return;
    }
    const ordenados = [...emails].reverse(); // la query viene date desc → imprimir asc
    const filas = ordenados.map((e) => {
      const autor = escapeHtml(e.from_name || e.from_email || "");
      const para = escapeHtml(e.to_email || "");
      const fecha = e.date ? format(new Date(e.date), "dd MMM yyyy, HH:mm", { locale: es }) : "Sin fecha";
      const dir = e.direction === "inbound" ? "Recibido" : "Enviado";
      const cuerpo = escapeHtml(e.body_text || "").replace(/\n/g, "<br>");
      return `<div class="email ${e.direction}">
        <div class="meta"><span class="dir">${dir}</span> · <strong>${autor}</strong>${para ? ` → ${para}` : ""} · <span class="date">${escapeHtml(fecha)}</span></div>
        <div class="subject">${escapeHtml(e.subject || "(sin asunto)")}</div>
        <div class="body">${cuerpo}</div>
      </div>`;
    }).join("");
    const cliente = escapeHtml(lead?.nombre || lead?.email || "");
    win.document.write(`<!DOCTYPE html>
<html lang="es"><head><meta charset="UTF-8">
<title>Conversación por email${cliente ? ` — ${cliente}` : ""}</title>
<style>
  @page { size: A4; margin: 16mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif; color:#1f2937; font-size:12px; line-height:1.5; padding:24px; }
  h1 { font-size:18px; border-bottom:2px solid #1f2937; padding-bottom:10px; margin:0 0 20px; }
  .email { border:1px solid #e5e7eb; border-radius:6px; padding:12px 14px; margin-bottom:14px; page-break-inside: avoid; }
  .email.outbound { background:#f5f3ff; border-color:#ddd6fe; }
  .meta { font-size:11px; color:#6b7280; margin-bottom:6px; }
  .dir { font-weight:600; color:#374151; }
  .subject { font-weight:700; margin-bottom:8px; }
  .body { white-space:pre-wrap; color:#374151; }
  @media print { body { padding:0; } }
</style></head>
<body>
  <h1>Conversación por email${cliente ? ` — ${cliente}` : ""}</h1>
  ${filas}
  <script>setTimeout(function(){ window.print(); }, 500);<\/script>
</body></html>`);
    win.document.close();
  };

  const handleSend = async () => {
    const plainText = (editorRef.current?.innerText || "").trim();
    if (!plainText) {
      toast({
        title: "Error",
        description: "Escribe un mensaje",
        variant: "destructive"
      });
      return;
    }

    setIsSending(true);
    try {
      const finalHtml = buildSendableHtml();
      const allAttachments = [
        ...attachments.map(a => ({ filename: a.filename, url: a.url })),
        ...pastedImages.map(img => ({
          filename: img.filename,
          content: img.dataUrl.split(',')[1] || '',
          contentType: img.contentType,
          cid: img.cid,
        })),
      ];

      await invokeFunctions('sendEmailIMAP', {
        lead_id: lead.id,
        to_email: lead.email,
        cc: cc.trim(),
        bcc: bcc.trim(),
        subject: subject || `Re: Consulta`,
        body: plainText,
        body_html: finalHtml,
        attachments: allAttachments,
        in_reply_to: replyToMessageId,
        references: references,
      });

      queryClient.invalidateQueries({ queryKey: ['emails', lead.id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', lead.id] });

      setShowCompose(false);
      setSubject("");
      setBodyHtml("");
      setCc("");
      setBcc("");
      setAttachments([]);
      setPastedImages([]);
      setReplyToMessageId(null);
      setReferences(null);
      if (editorRef.current) editorRef.current.innerHTML = "";

      toast({
        title: "Enviado",
        description: "Email enviado correctamente",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "No se pudo enviar el email",
        variant: "destructive"
      });
    }
    setIsSending(false);
  };

  const escapeHtml = (s) => String(s || "")
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Subject: garantiza un único "Re: " al inicio aunque el original tenga
  // "Re: Re:", "RE :", etc. No toca "Fwd:".
  const normalizeReplySubject = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return 'Re:';
    let stripped = s;
    while (true) {
      const m = stripped.match(/^\s*re\s*:\s*/i);
      if (!m) break;
      stripped = stripped.slice(m[0].length);
    }
    return stripped ? `Re: ${stripped}` : 'Re:';
  };

  // References (RFC 5322 §3.6.4): References del email padre + Message-ID del
  // padre. Si el padre no tiene References, References = solo el Message-ID.
  const buildReferencesChain = (parentEmail) => {
    if (!parentEmail) return null;
    const parts = [];
    if (parentEmail.references) {
      parts.push(String(parentEmail.references).trim());
    }
    if (parentEmail.message_id) {
      parts.push(parentEmail.message_id);
    }
    const joined = parts.filter(Boolean).join(' ').trim();
    return joined || null;
  };

  const buildQuoteHtml = (email) => {
    const dateStr = email.date ? format(new Date(email.date), "dd/MM/yyyy 'a las' HH:mm", { locale: es }) : '';
    const author = escapeHtml(email.from_name || email.from_email || '');
    const quoted = escapeHtml(email.body_text || '').replace(/\n/g, '<br>');
    return `<p><br></p><p><br></p><hr><p>El ${dateStr}, ${author} escribió:</p><blockquote style="border-left:2px solid #ddd; padding-left:8px; color:#666; margin:0;">${quoted}</blockquote>`;
  };

  const handleReply = (email) => {
    setSubject(normalizeReplySubject(email.subject));
    setBodyHtml(buildQuoteHtml(email));
    setCc("");
    setBcc("");
    setAttachments([]);
    setPastedImages([]);
    setReplyToMessageId(email.message_id || null);
    setReferences(buildReferencesChain(email));
    setShowCompose(true);
  };

  const handleNewThread = () => {
    setSubject("");
    setBodyHtml("");
    setCc("");
    setBcc("");
    setAttachments([]);
    setPastedImages([]);
    setReplyToMessageId(null);
    setReferences(null);
    setShowCompose(true);
  };

  // Continuar en el hilo existente (responder al último email INBOUND con message_id;
  // si no hay inbound usable, cae al último email cualquiera; si no hay nada, abre nuevo hilo).
  const handleContinueThread = () => {
    if (emails.length === 0) {
      handleNewThread();
      return;
    }
    const targetEmail =
      emails.find(e => e.direction === 'inbound' && e.message_id) ||
      emails.find(e => e.message_id) ||
      emails[0];
    if (!targetEmail) {
      handleNewThread();
      return;
    }
    setSubject(normalizeReplySubject(targetEmail.subject));
    setBodyHtml("");
    setCc("");
    setBcc("");
    setAttachments([]);
    setPastedImages([]);
    setReplyToMessageId(targetEmail.message_id || null);
    setReferences(buildReferencesChain(targetEmail));
    setShowCompose(true);
  };

  // #17 ¿Hay algo sobre lo que sugerir? Inbound, mensaje_original o meta_webhook.
  const hasInbound = emails.some(e => e.direction === 'inbound');
  const canSuggest = hasInbound || !!lead.mensaje_original || !!lead.meta_webhook;

  const handleSugerir = async () => {
    setIsSuggesting(true);
    setSugerencia(null);
    try {
      const { data } = await invokeFunctions('sugerirRespuestaEmail', { lead_id: lead.id });
      setSugerencia(data);
      if (!data?.matched) {
        toast({
          title: "Sin caso aplicable",
          description: data?.razonamiento || "Ningún caso configurado encaja con este email.",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error?.message || "No se pudo generar la sugerencia",
        variant: "destructive",
      });
    }
    setIsSuggesting(false);
  };

  const handleAcceptSugerencia = () => {
    if (!sugerencia) return;
    // Inyectar en el compose. Si hay inbound, enlazar el hilo (último inbound con message_id).
    const lastInbound = emails.find(e => e.direction === 'inbound' && e.message_id);
    const cuerpoHtml = String(sugerencia.cuerpo || '')
      .split('\n').map(l => l ? `<p>${l.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>` : '<p><br></p>').join('');

    // Si tenemos un inbound al que vinculamos, normalizamos el subject de la
    // sugerencia con "Re:" y construimos References como cadena real (las
    // sugerencias IA no siempre incluyen "Re:" en el asunto).
    setSubject(lastInbound ? normalizeReplySubject(sugerencia.asunto || lastInbound.subject) : (sugerencia.asunto || ''));
    setBodyHtml(cuerpoHtml);
    setCc("");
    setBcc("");
    setAttachments([]);
    setPastedImages([]);
    setReplyToMessageId(lastInbound?.message_id || null);
    setReferences(lastInbound ? buildReferencesChain(lastInbound) : null);
    setShowCompose(true);
    setSugerencia(null);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    try {
      for (const file of files) {
        // UploadFile (api/integrations.js) recibe el File directo y devuelve
        // { url, filename, path }. Antes se llamaba uploadFile({ file }) y
        // se leía file_url — ambas cosas mal, lo que rompía cualquier intento
        // de adjuntar archivo (caía siempre al catch → toast de error).
        const { url } = await UploadFile(file);
        setAttachments(prev => [...prev, { filename: file.name, url }]);
      }
    } catch (error) {
      console.error('Error subiendo adjunto:', error);
      toast({
        title: "Error",
        description: error?.message || "No se pudo subir el archivo",
        variant: "destructive"
      });
    }
    setIsUploading(false);
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Card className="dark:bg-gray-900 dark:border-gray-800">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-lg flex items-center gap-2 text-gray-900 dark:text-white">
            <Mail className="w-5 h-5" />
            Conversación por Email
          </CardTitle>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleSync}
              disabled={isSyncing}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
              Sincronizar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
            >
              <Printer className="w-4 h-4 mr-1" />
              Imprimir
            </Button>
            {/* "Responder" siempre visible: si hay hilo existente (al menos un emailMessage)
                continúa el hilo; si no, abre uno nuevo dirigido al lead. */}
            {canSuggest && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleSugerir}
                disabled={isSuggesting}
                title="Sugerir respuesta con IA"
              >
                {isSuggesting ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-1 text-amber-500" />
                )}
                Sugerir respuesta
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={handleContinueThread}
            >
              <Send className="w-4 h-4 mr-1" />
              Responder
            </Button>
            <Button
              size="sm"
              onClick={handleNewThread}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Send className="w-4 h-4 mr-1" />
              Nuevo hilo
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {sugerencia && sugerencia.matched && (
          <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                  Sugerencia IA
                </span>
                {sugerencia.caso?.nombre && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200">
                    {sugerencia.caso.nombre}
                  </span>
                )}
              </div>
              <button
                onClick={() => setSugerencia(null)}
                className="text-amber-400 hover:text-amber-600"
                title="Descartar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {sugerencia.razonamiento && (
              <p className="text-xs text-amber-700 dark:text-amber-300 italic">
                {sugerencia.razonamiento}
              </p>
            )}
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Asunto:</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {sugerencia.asunto}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Cuerpo:</div>
              <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-sans bg-white dark:bg-gray-900 p-3 rounded border border-amber-100 dark:border-amber-900 max-h-[300px] overflow-y-auto">
{sugerencia.cuerpo}
              </pre>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setSugerencia(null)}>
                Descartar
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptSugerencia}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Check className="w-4 h-4 mr-1" />
                Aceptar y editar
              </Button>
            </div>
          </div>
        )}
        {showCompose && (
          <div className="mb-4 p-4 bg-indigo-50 dark:bg-indigo-950 rounded-lg border border-indigo-200 dark:border-indigo-800 space-y-3">
            {replyToMessageId && (
              <div className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
                <ArrowDownLeft className="w-3 h-3" />
                Respondiendo en el hilo existente
              </div>
            )}
            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400">Para:</label>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{lead.email}</p>
            </div>

            {/* CC */}
            <div className="relative">
              <div className="flex items-center gap-1">
                <label className="text-xs text-gray-500 dark:text-gray-400 w-10">CC:</label>
                <Input
                  placeholder="email1@... , email2@... (opcional)"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  className="dark:bg-gray-900 dark:border-gray-700 flex-1"
                />
                <Button type="button" variant="ghost" size="sm" onClick={() => { setShowCcPicker(v => !v); setShowBccPicker(false); }} title="Añadir usuario CRM">
                  <UserPlus className="w-4 h-4" />
                </Button>
              </div>
              {showCcPicker && crmUsers.length > 0 && (
                <div className="absolute z-10 right-0 mt-1 w-64 max-h-48 overflow-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
                  {crmUsers.filter(u => u.email).map(u => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => { addAddressToField('cc', u.email); setShowCcPicker(false); }}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <span className="font-medium">{u.nombre || u.email}</span>
                      <span className="text-gray-500 ml-1">{u.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* BCC */}
            <div className="relative">
              <div className="flex items-center gap-1">
                <label className="text-xs text-gray-500 dark:text-gray-400 w-10">BCC:</label>
                <Input
                  placeholder="email1@... , email2@... (opcional)"
                  value={bcc}
                  onChange={(e) => setBcc(e.target.value)}
                  className="dark:bg-gray-900 dark:border-gray-700 flex-1"
                />
                <Button type="button" variant="ghost" size="sm" onClick={() => { setShowBccPicker(v => !v); setShowCcPicker(false); }} title="Añadir usuario CRM">
                  <UserPlus className="w-4 h-4" />
                </Button>
              </div>
              {showBccPicker && crmUsers.length > 0 && (
                <div className="absolute z-10 right-0 mt-1 w-64 max-h-48 overflow-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
                  {crmUsers.filter(u => u.email).map(u => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => { addAddressToField('bcc', u.email); setShowBccPicker(false); }}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <span className="font-medium">{u.nombre || u.email}</span>
                      <span className="text-gray-500 ml-1">{u.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Input
              placeholder="Asunto"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="dark:bg-gray-900 dark:border-gray-700"
            />

            {/* Editor HTML básico */}
            <div className="border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900">
              <div className="flex flex-wrap gap-1 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-1">
                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => exec('bold')} title="Negrita (Ctrl+B)"><Bold className="w-4 h-4" /></Button>
                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => exec('italic')} title="Cursiva (Ctrl+I)"><Italic className="w-4 h-4" /></Button>
                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => exec('underline')} title="Subrayado (Ctrl+U)"><Underline className="w-4 h-4" /></Button>
                <span className="w-px bg-gray-300 dark:bg-gray-600 my-1 mx-1" />
                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={promptLink} title="Insertar enlace"><Link2 className="w-4 h-4" /></Button>
                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => exec('insertUnorderedList')} title="Lista de viñetas"><List className="w-4 h-4" /></Button>
                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => exec('insertOrderedList')} title="Lista numerada"><ListOrdered className="w-4 h-4" /></Button>
                <span className="ml-auto text-[10px] text-gray-400 self-center pr-1 hidden md:inline">Pega capturas con Ctrl+V</span>
              </div>
              <div
                ref={editorRef}
                contentEditable
                onPaste={handlePaste}
                onInput={(e) => setBodyHtml(e.currentTarget.innerHTML)}
                className="min-h-[160px] max-h-[400px] overflow-y-auto p-3 outline-none text-sm text-gray-900 dark:text-white"
                style={{ wordBreak: 'break-word' }}
                suppressContentEditableWarning
              />
            </div>

            {(attachments.length > 0 || pastedImages.length > 0) && (
              <div className="flex flex-wrap gap-2">
                {attachments.map((att, index) => (
                  <div key={`f-${index}`} className="flex items-center gap-1 bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-200 dark:border-gray-700 text-xs">
                    <Paperclip className="w-3 h-3 text-gray-400" />
                    <span className="max-w-[150px] truncate">{att.filename}</span>
                    <button onClick={() => removeAttachment(index)} className="ml-1 text-gray-400 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {pastedImages.map((img, index) => (
                  <div key={`p-${index}`} className="flex items-center gap-1 bg-indigo-50 dark:bg-indigo-950 px-2 py-1 rounded border border-indigo-200 dark:border-indigo-800 text-xs">
                    <Paperclip className="w-3 h-3 text-indigo-400" />
                    <span className="max-w-[180px] truncate">{img.filename} (inline)</span>
                    <button
                      onClick={() => setPastedImages(prev => prev.filter((_, i) => i !== index))}
                      className="ml-1 text-indigo-400 hover:text-red-500"
                      title="Eliminar (la imagen sigue en el editor — bórrala también con Backspace)"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 justify-between">
              <div className="flex items-center">
                <input
                  type="file"
                  id="file-attachment"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('file-attachment').click()}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Paperclip className="w-4 h-4 mr-1" />
                      Adjuntar
                    </>
                  )}
                </Button>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowCompose(false)}>
                  Cancelar
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleSend}
                  disabled={isSending}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {isSending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-1" />
                      Enviar
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : emails.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Mail className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No hay emails con este lead</p>
            <p className="text-xs mt-1">Sincroniza para buscar emails existentes</p>
          </div>
        ) : (
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-3">
              {emails.map((email) => (
                <div
                  key={email.id}
                  className={`p-4 rounded-lg border ${
                    email.direction === 'inbound'
                      ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 mr-8'
                      : 'bg-indigo-50 dark:bg-indigo-950 border-indigo-200 dark:border-indigo-800 ml-8'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {email.direction === 'inbound' ? (
                        <ArrowDownLeft className="w-4 h-4 text-green-500" />
                      ) : (
                        <ArrowUpRight className="w-4 h-4 text-indigo-500" />
                      )}
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        {email.direction === 'inbound' ? email.from_name || email.from_email : 'Tú'}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        • {email.date ? format(new Date(email.date), "dd MMM yyyy, HH:mm", { locale: es }) : 'Sin fecha'}
                      </span>
                      {/* Iconos de tracking para emails salientes */}
                      {email.direction === 'outbound' && (
                        <TooltipProvider>
                          <div className="flex items-center gap-1 ml-1">
                            {email.opened && (
                              <Tooltip>
                                <TooltipTrigger>
                                  <Eye className="w-3.5 h-3.5 text-blue-500" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Abierto{email.opened_at ? ` el ${format(new Date(email.opened_at), "dd/MM/yyyy HH:mm")}` : ''}</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {email.clicked && (
                              <Tooltip>
                                <TooltipTrigger>
                                  <MousePointerClick className="w-3.5 h-3.5 text-purple-500" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Clic en enlace{email.clicked_at ? ` el ${format(new Date(email.clicked_at), "dd/MM/yyyy HH:mm")}` : ''}</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {!email.opened && !email.clicked && !email.bounced && (
                              <Tooltip>
                                <TooltipTrigger>
                                  <Check className="w-3.5 h-3.5 text-gray-400" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Enviado</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TooltipProvider>
                      )}
                    </div>
                    <a
                      href={buildWebmailUrl(lead.email)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-indigo-500 transition-colors"
                      title={lead.email ? `Abrir webmail buscando ${lead.email}` : 'Abrir webmail'}
                    >
                      <ExternalLink className="w-3 h-3" />
                      <span className="hidden sm:inline">Webmail</span>
                    </a>
                  </div>
                  
                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    {email.subject}
                  </p>
                  
                  <p className={`text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap ${!expandedEmails[email.id] ? 'line-clamp-4' : ''}`}>
                    {email.body_text}
                  </p>

                  <div className="flex items-center gap-2 mt-2">
                    {email.body_text && email.body_text.length > 200 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs p-0 h-auto text-gray-500 hover:text-gray-700"
                        onClick={() => toggleEmailExpanded(email.id)}
                      >
                        {expandedEmails[email.id] ? (
                          <>
                            <ChevronUp className="w-3 h-3 mr-1" />
                            Ver menos
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-3 h-3 mr-1" />
                            Ver todo
                          </>
                        )}
                      </Button>
                    )}
                    {email.direction === 'inbound' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs"
                        onClick={() => handleReply(email)}
                      >
                        <Send className="w-3 h-3 mr-1" />
                        Responder
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}